import React, {
	ComponentType,
	Suspense,
	useEffect,
	useLayoutEffect,
	useState,
} from "react";
import {
	matchRoutes,
	Route as RouteComponent,
	unstable_HistoryRouter as RouterComponent,
	Routes,
} from "react-router-dom";
import { createBrowserHistory, Location, parsePath } from "history";
import NProgress from "accessible-nprogress";
import * as Utils from "./Utils";
import * as StringUtils from "./StringUtils";
import * as AnalyticsUtils from "./AnalyticsUtils";
import * as EnvironmentUtils from "./EnvironmentUtils";
import { RouterHistory } from "../types/MiscTypes";
import { Fleet } from "lib/types/Fleet";

// can only block nav for 4 seconds
const MAX_BLOCK_TIME = 4000;

export type RoutePromise = Promise<{
	default: ComponentType<any>;
}>;

export type RouteLoader = (() => RoutePromise) & {
	__resolvedTo?: React.ReactElement;
};

export type RoutingComponent = React.ComponentType<any> | RouteLoader;

export type RouteTemplate<O extends object = {}> = Omit<
	RouteConfig<O>,
	"template" | "component" | "path" | "title"
>;

export interface RouteConfig<O extends object = {}> {
	path: string | string[];
	component: RoutingComponent;
	title?: string;
	titleFormatter?: (location: Location) => string;
	template?: RouteTemplate<O>;
	options?: O;
}

export enum RoutingMiddlewareResultType {
	Redirect = "redirect",
	Continue = "continue",
}

export type RoutingMiddlewareResult =
	| RoutingMiddlewareResultType.Continue
	| {
			type: RoutingMiddlewareResultType.Redirect;
			location: string;
			includeRedirect?: boolean;
	  };

export type RouterMiddleware<O extends object = {}> = (
	route: Route<O> | null,
	checkedFleet?: Fleet,
) => RoutingMiddlewareResult;

export interface RouterConfig<P extends string, O extends object = {}> {
	name: string;
	middleware?: RouterMiddleware<O>[];
	routes: Record<P, RouteConfig<O>>;
}

export interface RouterProps {
	children: (routes: React.ReactNode) => React.ReactNode;
}

export interface RouterManager<P extends string = string, O extends object = {}>
	extends RouterConfig<P, O> {
	routes: Record<P, Route<O>>;
	routeList: Route<O>[];
	component: React.FC<RouterProps>;
}

export interface Route<O extends object = {}> extends RouteConfig<O> {
	id: string;
	__wrapped?: React.ReactElement;
	caseSensitive?: boolean;
	element?: Element;
}

export interface RedirectOptions {
	location: string;
	includeRedirect?: boolean;
	includeFrom?: boolean;
	replace?: boolean;
	event?: { ctrlKey?: boolean; metaKey?: boolean };
	force?: boolean;
	sameWindow?: boolean;
}

const env = EnvironmentUtils.getAppEnvironment();
export const LANDING_PAGE_URL = `https://${
	env && !EnvironmentUtils.isProduction() ? env : "www"
}.brightmile.io`;

NProgress.configure({
	showSpinner: false,
});

const blockers: Promise<any>[] = [];

const routePromises: Record<string, Promise<React.ComponentType<any>>> = {};

export function block<T>(name: string, blocker: Promise<T>): Promise<T> {
	const startTimestamp = Date.now();
	blockers.push(
		blocker.then(
			(data) => {
				blockers.splice(blockers.indexOf(blocker), 1);
				if (process.env.NODE_ENV === "development") {
					// eslint-disable-next-line
					console.debug(
						`Navigation block '${name}' lasted ${
							Date.now() - startTimestamp
						}ms`,
					);
				}
				return data;
			},
			(error) => {
				throw error;
			},
		),
	);
	return blocker;
}

export const createConfig = <O extends object, P extends string>(
	config: RouterConfig<P, O>,
) => {
	return config;
};

export const createRouterManager = <P extends string, O extends object = {}>(
	config: RouterConfig<P, O>,
): RouterManager<P, O> => {
	let history = getHistory();

	const routeList: Route<O>[] = [];
	const routerManager: RouterManager<P, O> = Object.assign(config, {
		routeList: routeList,
		routes: Object.keys(config.routes).reduce((routes, id) => {
			const routeConfig = (config.routes as any)[id];
			const { titleFormatter } = routeConfig;
			routeList.push(
				(routes[id] = Utils.deepMerge(
					routeConfig.template || {},
					Object.assign(routeConfig, {
						id: id,
						titleFormatter: titleFormatter,
					}),
				)),
			);
			return routes;
		}, {} as Record<string, Route>),
	}) as any;

	if (!history) {
		history = createHistory(routerManager);
	}

	const routesElements = createRoutesElements(routerManager);
	routerManager.component = React.memo((props: RouterProps) => {
		const { children } = props;

		return (
			<RouterComponent history={history}>
				{children(routesElements)}
			</RouterComponent>
		);
	});
	return routerManager;
};

// TODO: Cleanup this whole class

export type RouteChangeListener = (route: Route, location: Location) => void;
export type HistoryChangeListener = (location: Location) => void;
export type HashChangeListener = (newHash: string | null) => void;

const routeListeners: Record<string, RouteChangeListener> = {};

const historyListeners: Record<string, HistoryChangeListener> = {};

const hashListeners: Record<
	string,
	{ listener: HashChangeListener; path: string }
> = {};

let oldLocation: Location | null = null;
let oldRoute: Route | null = null;

const defaultRefreshHandler = (force?: boolean) => {
	(window.location as any).reload(Boolean(force));
};

let refreshHandler = defaultRefreshHandler;

export const refresh = (force?: boolean) => {
	refreshHandler(force);
	oldLocation = getHistory().location;
};

export const provideRefreshHandler = (handler: (force?: boolean) => void) => {
	refreshHandler = handler;
};

const createHistory = <P extends string, O extends object = {}>(
	routerManager: RouterManager<P, O>,
) => {
	if (window.__history) {
		throw new Error("Trying to create history twice");
	}

	const history = (window.__history = createBrowserHistory());
	(history as any).router = routerManager;

	// We want to control scroll restoration ourselves
	if ("scrollRestoration" in window.history) {
		// Back off, browser, I got this...
		window.history.scrollRestoration = "manual";
	}

	installBlocker();
	oldLocation = history.location;

	const route = getRouteForPath(history.location.pathname);
	oldRoute = route;
	if (runMiddlewareForRoute(route)) {
		routeInit(route);
	}

	// on subsuequent page navigations
	history.listen((event) => {
		installBlocker();

		const location = event.location;
		const route = getRouteForPath(location.pathname);
		if (getHref(location) !== getHref(oldLocation)) {
			routeInit(route);
			triggerHistoryListeners(location);

			if (route) {
				if (oldLocation && location.hash !== oldLocation.hash) {
					triggerHashListeners(
						route,
						location.hash ? location.hash.substring(1) : null,
					);
				}
				AnalyticsUtils.trackPage();
				if (!oldRoute || oldRoute.path !== route.path) {
					oldRoute = route;
				}
			}

			// make sure we do a deep copy
			oldLocation = { ...location };
		}
	});

	return history;
};

export function getHref<T extends Location | null>(
	location: T,
): T extends null ? null : string {
	return (
		location === null
			? null
			: `${location.pathname}${location.search}${location.hash}`
	) as any;
}

export const getHistory = (): RouterHistory => {
	return window.__history;
};

let requiresTopScroll = false;

const triggerRouteListeners = (route: Route, location: Location) => {
	Object.values(routeListeners).forEach((listener) =>
		listener(route, location),
	);
	requiresTopScroll = true;
};

export const onRouteChange = (listener: RouteChangeListener): string => {
	const id = StringUtils.uuid();
	routeListeners[id] = listener;
	return id;
};

export const clearRouteChange = (id: string) => {
	delete routeListeners[id];
};

const triggerHistoryListeners = (location: Location) => {
	Object.values(historyListeners).forEach((listener) => listener(location));
};

export const onHistoryChange = (listener: HistoryChangeListener): string => {
	const id = StringUtils.uuid();
	historyListeners[id] = listener;
	return id;
};

export const clearHistoryChange = (id: string) => {
	delete historyListeners[id];
};

export const clearHashChange = (id: string) => {
	delete hashListeners[id];
};

export const setHash = (hash: string | null, updateHistory = false) => {
	if (getHash() === hash) {
		return;
	}

	const func = updateHistory ? getHistory().push : getHistory().replace;

	const url = new URL(window.location.href);
	url.hash = hash || "";
	func(url.toString());
};

export const getHash = (): string | null => {
	return window.location.hash.substring(1) || null;
};

export const onHashChange = (path: string, listener: HashChangeListener) => {
	const id = StringUtils.uuid();
	hashListeners[id] = { listener, path };
	return id;
};

const triggerHashListeners = (route: Route, newHash: string | null) => {
	Object.values(hashListeners).forEach((data) => {
		if (!data.path || data.path === "*" || isRouteForPath(route, data.path)) {
			data.listener(newHash);
		}
	});
};

export const link = (
	url: string,
	e: (Event | React.SyntheticEvent) & {
		ctrlKey?: boolean;
		metaKey?: boolean;
	},
	options?: Partial<RedirectOptions>,
) => {
	if (!url) return;
	if (url.startsWith("mailto:")) {
		return;
	}

	if (e) {
		e.preventDefault();
		e.stopPropagation();

		if (e.ctrlKey || e.metaKey) {
			return window.open(url, "_blank");
		}
	}

	if (options?.sameWindow === false) {
		return window.open(url, "_blank");
	}

	if (url.startsWith("#")) {
		getHistory().replace(url);
	} else {
		go(Object.assign(options || {}, { location: url }));
	}
};

const routeInit = (route: Route | null) => {
	if (EnvironmentUtils.isTest()) {
		return null;
	}

	// TODO : Find a better place for these
	document.title = getRouteTitle(route);

	if (route) {
		if (!oldRoute || oldRoute.path !== route.path) {
			triggerRouteListeners(route, getHistory().location);
			startTransitions();
		}
	}

	return route;
};

export const getCurrentRoute = () => {
	return getRouteForPath(window.location.pathname);
};

export const getRouteName = (route: Route | null, current = false) => {
	let name = route?.title;
	if (route?.titleFormatter && current) {
		name = route?.titleFormatter(getHistory().location);
	}

	if (!name && route && current) {
		const parts = getHistory().location.pathname.split("/");
		const lastPart = parts
			.reverse()
			.find((string: string) => !string.startsWith(":"));
		name = lastPart ? StringUtils.toTitleCase(lastPart) : undefined;
	}

	if (!name && route) {
		name = StringUtils.toTitleCase(route.id);
	}

	return name;
};

export const getRouteTitle = (route: Route | null) => {
	let prefix = getHistory().router?.name;

	if (!EnvironmentUtils.isProduction()) {
		let envName = EnvironmentUtils.getAppEnvironmentDisplayName();
		envName = StringUtils.toTitleCase(envName, true);
		prefix += ` ${envName}`;
	}

	const name = getRouteName(route, true);
	return `${prefix}${name ? " | " + name : ""}`;
};

export const isRouteForPath = (route: RouteConfig, path: string): boolean => {
	let parsedPath = path;
	if (parsedPath.startsWith("/")) {
		parsedPath = parsedPath.substr(1);
	}
	parsedPath = parsedPath.split("#")[0];
	const parts = parsedPath.split("/");
	return (Array.isArray(route.path) ? route.path : [route.path]).some(
		(otherPath) => {
			let parsedOtherPath = otherPath;
			if (parsedOtherPath.startsWith("/")) {
				parsedOtherPath = parsedOtherPath.substr(1);
			}
			const otherParts = parsedOtherPath.split("/");

			return otherParts.every((otherPart, index) => {
				const part = parts[index];
				if (otherPart === undefined) {
					return false;
				}
				return (
					part === otherPart || otherPart.startsWith(":") || otherPart === "*"
				);
			});
		},
	);
};

export const getRouteForPath = (path: string): Route | null => {
	if (!path || path[0] === "#" || path[0] === "?") return null;
	const matches = matchRoutes(getHistory().router?.routeList as any[], path);
	return !matches || matches.length === 0
		? null
		: getHistory().router?.routes[(matches[0].route as Route).id] || null;
};

const createRoutesElements = <P extends string, O extends object = {}>(
	router: RouterManager<P, O>,
) => {
	return (
		<Routes>
			{router.routeList.map((route) => {
				if (!route.__wrapped) {
					route.__wrapped = React.createElement(routeProxy(route.component));
				}
				return (
					<RouteComponent
						key={route.id}
						path={route.path as string}
						element={route.__wrapped}
					/>
				);
			})}
		</Routes>
	);
};

export const runMiddlewareForRoute = (
	route: Route | null,
	checkedFleet?: Fleet,
) => {
	// run middleware, to see if a redirect might occur
	const middlewares = getHistory().router?.middleware;
	if (middlewares) {
		for (const middleware of middlewares) {
			const result = middleware(route, checkedFleet);
			const parsedResult =
				typeof result === "string" ? { type: result } : result;
			if (parsedResult.type === RoutingMiddlewareResultType.Redirect) {
				go({
					replace: true,
					location: parsedResult.location,
					includeRedirect: parsedResult.includeRedirect,
				});
				return false;
			}
		}
	}
	return true;
};

const routesLoaded: Record<string, React.ComponentType<any>> = {};

const loadRoute = async (route: Route): Promise<React.ComponentType<any>> => {
	if ("$$typeof" in route.component) {
		return route.component as any;
	}

	const alreadyLoaded = routesLoaded[route.id];
	if (alreadyLoaded) return alreadyLoaded;
	const routePromise = routePromises[route.id];
	if (routePromise) return routePromise;

	const routeLoader = route.component as RouteLoader;
	// eslint-disable-next-line
	return (routePromises[route.id] = routeLoader().then((result) => {
		const component = result.default;
		routeLoader.__resolvedTo = React.createElement(component);
		routesLoaded[route.id] = component;
		// console.log(`Loaded '${route.id}'`);
		return component;
	}));
};

let installed = false;

const installBlocker = () => {
	if (installed) return;
	installed = true;

	// TODO: find a way to not do this, really annoyingly history.block adds a beforeunload listener
	const originalAddEventListener = window.addEventListener;
	window.addEventListener = Utils.noop;
	const unblock = getHistory().block((tx) => {
		const { location } = tx;
		const route = getRouteForPath(location.pathname);

		let alreadyLoadedTimeout: any;
		let loaded = false;

		const allow = () => {
			installed = false;
			unblock();
			tx.retry();
		};

		const attemptAllow = () => {
			if (loaded) return;
			loaded = true;
			clearTimeout(alreadyLoadedTimeout);
			if (runMiddlewareForRoute(route)) {
				allow();
			}
		};

		if (route) {
			// eslint-disable-next-line
			Promise.all([loadRoute(route), ...blockers]).then(attemptAllow);
			alreadyLoadedTimeout = setTimeout(attemptAllow, MAX_BLOCK_TIME);
		} else {
			allow();
		}
	});
	window.addEventListener = originalAddEventListener;
};

let initialRender = false;

const routeProxy = (component: RoutingComponent): React.ElementType => {
	const loader: RouteLoader | undefined =
		"$$typeof" in component ? undefined : (component as any);
	const element = React.createElement(
		(loader ? React.lazy(loader) : component) as any,
	);

	return Object.assign(
		React.memo(() => {
			const [waitForBlockers, setWaitForBlockers] = useState(
				!initialRender && blockers.length > 0,
			);

			useLayoutEffect(() => {
				finishTransitions();

				if (requiresTopScroll) {
					requiresTopScroll = false;
					const y = 0;
					window.scrollTo({
						top: y,
					});
				}
			});

			// wait for initial blockers for the first route to render
			useEffect(() => {
				if (waitForBlockers) {
					const blockersToWaitFor = [...blockers];
					Promise.race([
						new Promise((resolve) => setTimeout(resolve, MAX_BLOCK_TIME)),
						Promise.all(blockersToWaitFor),
					]).then((data) => {
						initialRender = true;
						setWaitForBlockers(false);
						return data;
					});
				} else {
					initialRender = true;
				}
			}, [waitForBlockers]);

			if (waitForBlockers) {
				return null;
			}

			if (!loader) {
				return element;
			} else if (loader && loader.__resolvedTo) {
				return loader.__resolvedTo;
			}

			return <Suspense fallback={""}>{element}</Suspense>;
		}),
		{
			name: `ProxiedRoute${
				(component as any).name ? `[${(component as any).name}]` : ""
			}`,
		},
	);
};

export const setQueryParams = (
	obj: Record<string, string | undefined>,
	push?: boolean,
) => {
	try {
		const url = new URL(window.location.href);
		if (url && url.searchParams) {
			Object.keys(obj).forEach((key) => {
				const value = obj[key];
				if (value === undefined) {
					url.searchParams.delete(key);
				} else {
					url.searchParams.set(key, JSON.stringify(value));
				}
			});
			const hash = window.location.hash;
			const func = push ? getHistory().push : getHistory().replace;
			func(`${url.pathname}?${url.searchParams.toString()}${hash}`);
		}
	} catch (error) {
		Utils.oldBrowserError(error);
	}
};

export const go = (options: RedirectOptions | string): Promise<void> => {
	let parsedOptions = options;
	if (typeof parsedOptions === "string") {
		parsedOptions = {
			location: parsedOptions,
		};
	} else if (parsedOptions.event) {
		// This prevents an infinite loop...
		parsedOptions.event = {
			ctrlKey: parsedOptions.event.ctrlKey,
			metaKey: parsedOptions.event.metaKey,
		};
	}

	if (!Utils.isSafeUrl(parsedOptions.location)) {
		console.error(
			`[brightmile] Unsafe url passed: '${parsedOptions.location}'`,
		);
		return Promise.resolve();
	}

	// Only works for basic urls atm
	if (parsedOptions.includeRedirect) {
		parsedOptions.location += `?redirect=${btoa(getCurrentPath())}`;
	}

	if (parsedOptions.includeFrom) {
		parsedOptions.location += `?fromUrl=${btoa(
			getCurrentPath(),
		)}&fromName=${btoa(document.title.split("|")[1].trim())}`;
	}

	let external = parsedOptions.location.startsWith("https://");
	if (
		parsedOptions.event &&
		(parsedOptions.event.ctrlKey || parsedOptions.event.metaKey)
	) {
		external = true;
	}

	if (external) {
		const anchor = document.createElement("a");
		anchor.setAttribute("href", parsedOptions.location);

		if (!parsedOptions.sameWindow) {
			anchor.setAttribute("target", "_blank");
		}

		anchor.click();
	} else {
		const currentLocation = window.location.href.substr(
			window.location.origin.length,
		);
		if (
			parsedOptions.force ||
			currentLocation.toLowerCase() !== parsedOptions.location.toLowerCase()
		) {
			const path = parsePath(parsedOptions.location);
			// fix issue with history not blanking these
			if (path.hash === undefined) {
				path.hash = "";
			}
			if (path.search === undefined) {
				path.search = "";
			}
			return new Promise<void>((resolve) => {
				const id = onHistoryChange(() => {
					resolve();
					clearHistoryChange(id);
				});
				getHistory()[parsedOptions.replace ? "replace" : "push"](path, {});
			});
		}
	}
	return Promise.resolve();
};

export const isQueryParam = (name: string): boolean => {
	const param = getQueryParam(name);
	return Boolean(param);
};

export const removeQueryParam = (name: string) => {
	return setQueryParams({ [name]: undefined });
};

export const getQueryParam = (name: string) => {
	try {
		const url = new URL(window.location.href);
		if (url && url.searchParams) {
			const param = url.searchParams.get(name);
			if (typeof param === "string") {
				// Attempt decode
				try {
					return JSON.parse(decodeURIComponent(param));
				} catch (error) {
					return param;
				}
			}
		}
	} catch (error) {
		Utils.oldBrowserError(error);
	}
	return undefined;
};

export const getCurrentPath = () => {
	return getPathForLocation(getHistory().location);
};

export const getPathForLocation = (location: Location) => {
	return `${location.pathname}${location.hash}${location.search}`;
};

export const getRedirectLocation = () => {
	let url: string | null = null;
	const from = getQueryParam("redirect");
	try {
		url = from ? atob(from) : null;
	} catch (error) {
		console.warn("[brightmile] Redirect URL was invalid");
	}
	if (url) {
		if (Utils.isRelativeUrl(url)) {
			if (Utils.isSafeUrl(url)) {
				return url;
			} else {
				console.error("[brightmile] Redirect URL was unsafe");
			}
		} else {
			console.error("[brightmile] Redirect URL was not relative");
		}
	}

	return null;
};

export function getQueryParams<T extends Record<string, string>>(
	names: (keyof T)[],
): T {
	return names.reduce((obj, name) => {
		obj[name] = getQueryParam(name as string);
		return obj;
	}, {} as T);
}

export const getCurrentParams = () => {
	return getParams(getCurrentRoute());
};

export const getParams = (route: Route | null): any => {
	if (!route) {
		return {};
	}

	const match = matchRoutes([route as any], window.location.pathname);
	if (!match || match.length === 0) return {};
	return match[0].params;
};

let transitionTimeout: any = null;

export const finishTransitions = () => {
	transitionTimeout = setTimeout(() => NProgress.done(), 200);
	document.body.classList.toggle("--transition", false);
};

export const startTransitions = () => {
	clearTimeout(transitionTimeout);
	NProgress.start();
	document.body.classList.toggle("--transition", true);
};

const preloadQueue: Route[] = [];
const preloadedRoutes: Record<string, boolean> = {};

// the pre-loader pre loads routes that can be linked to on the page
// it runs every 2 seconds starting 2 seconds after the initial page load
export const initPreloader = (waitFor = 2000) => {
	setTimeout(() => {
		setInterval(() => {
			const route = preloadQueue[0];
			preloadQueue.splice(0, 1);
			if (route) {
				loadRoute(route);
			}
		}, 2000);
	}, waitFor);
};

export const preload = (route: Route) => {
	if (
		!routesLoaded[route.id] &&
		!routePromises[route.id] &&
		!preloadedRoutes[route.id]
	) {
		// eslint-disable-next-line
		console.debug(`Preloading '${route.id}'`);
		preloadedRoutes[route.id] = true;
		preloadQueue.push(route);
	}
};
